import type { MessageRow } from '../lib/api';

interface Props {
  prompt: MessageRow;
}

/**
 * InteractivePromptCard.
 *
 * Display-only. Renders inline when an `interactive_prompt` row arrives
 * from a dispatch — i.e., when the spawned claude emitted an
 * AskUserQuestion tool_use event during its run. The card surfaces what
 * the agent considered asking, but does NOT collect or relay an answer.
 *
 * Why no answer field: claude --print's runtime auto-denies
 * AskUserQuestion regardless of permission mode (see
 * daemon/INTERACTIVE-DESIGN.md). Mid-run human decisions go through the
 * worker-tier Article 1.5 broker (AuthorizeCard) instead. Displaying the
 * prompt is still useful operator-feedback: you can see what the agent
 * was reasoning about even if the runtime didn't let it ask.
 */
export function InteractivePromptCard({ prompt }: Props) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="self-start max-w-[75%] md:max-w-[600px] rounded-md px-3 py-2 border-l-2"
      style={{
        background: 'var(--agent-bg-soft)',
        borderColor: 'var(--agent-edge-soft)',
      }}
    >
      <div
        className="font-mono text-[10px] uppercase tracking-mono-wide mb-1"
        style={{ color: 'var(--agent-fg-soft)' }}
      >
        agent considered asking
      </div>
      <div
        className="text-[13px] whitespace-pre-wrap"
        style={{ color: 'var(--agent-fg-body)' }}
      >
        {prompt.content || '(empty prompt)'}
      </div>
      <div
        className="mt-1 font-mono text-[10px] tracking-mono-wide"
        style={{ color: 'var(--agent-subtle)' }}
      >
        not answered · runtime denies AskUserQuestion in headless mode · use the Article 1.5 broker for write decisions
      </div>
    </div>
  );
}
